import React, { useRef, useEffect } from "react";
import { useMessages } from "../../../hooks/useMessages";
import { useAppSelector } from "../../../hooks/redux-hooks";
import { useUserData } from "../../../hooks/useUsers";
import { Typography, Avatar, Button, Divider, Tag } from "antd";
import { ArrowLeftOutlined, UserOutlined, HeartOutlined, PhoneOutlined, VideoCameraOutlined } from "@ant-design/icons";
import MessageComposer from "./MessageComposer";
import MessageCard from "./MessageCard";

const { Title, Text } = Typography;

const ConversationThread = () => {
  const messages = useMessages();
  const currentChat = useAppSelector((state) => state.currentChat);
  const { userData: recipientData } = useUserData(currentChat.userID);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const messageCount = messages?.length || 0;

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(145deg, #f8f9ff 0%, #ffffff 100%)'
    }}>
      
      {/* Conversation Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Avatar 
              size={48}
              src={recipientData?.profileImages?.[0]?.url || `https://api.dicebear.com/7.x/miniavs/svg?seed=${recipientData?.uid}`}
              style={{ 
                border: '3px solid rgba(102, 126, 234, 0.2)' 
              }}
            />
            <div>
              <Title level={4} style={{ 
                margin: '0 0 4px 0', 
                color: '#2c3e50',
                fontWeight: '600'
              }}>
                {recipientData?.firstName} {recipientData?.lastName}
              </Title>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Tag color="blue" size="small">
                  {recipientData?.age || 25} years
                </Tag>
                <Tag color="green" size="small">
                  {recipientData?.currentResidence}
                </Tag>
                <Text style={{ color: '#64748b', fontSize: '12px' }}>
                  {messageCount} messages exchanged
                </Text>
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              type="text" 
              icon={<UserOutlined />}
              style={{ borderRadius: '8px' }}
              title="View Profile"
            />
            <Button 
              type="text" 
              icon={<PhoneOutlined />}
              style={{ borderRadius: '8px' }}
              title="Request Contact"
            />
            <Button 
              type="text" 
              icon={<VideoCameraOutlined />}
              style={{ borderRadius: '8px' }}
              title="Video Call"
            />
          </div>
        </div>

        {/* Compatibility Hint */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          padding: '12px 16px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <HeartOutlined style={{ color: '#667eea' }} />
          <Text style={{ color: '#667eea', fontSize: '13px', fontWeight: '500' }}>
            You both are interested in traditional values and family-oriented lifestyle
          </Text>
        </div>
      </div>

      {/* Messages Thread */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {messages?.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#64748b'
          }}>
            <HeartOutlined style={{ fontSize: '48px', marginBottom: '16px', color: '#667eea' }} />
            <Title level={4} style={{ color: '#64748b', marginBottom: '8px' }}>
              Start Your Conversation
            </Title>
            <Text style={{ color: '#94a3b8' }}>
              Take your time to craft a thoughtful message. Quality conversations lead to meaningful connections.
            </Text>
          </div>
        ) : (
          <>
            {messages?.map((message, index) => (
              <MessageCard 
                key={message.id} 
                message={message}
                isFirst={index === 0}
                recipientData={recipientData}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Message Composer */}
      <div style={{
        padding: '16px 24px 24px 24px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderTop: '1px solid rgba(0, 0, 0, 0.08)'
      }}>
        <MessageComposer recipientName={`${recipientData?.firstName} ${recipientData?.lastName}`} />
      </div>
    </div>
  );
};

export default ConversationThread;